/**
 * Payment Context
 *
 * Manages payment state across the booking flow:
 * - User email for payment (with session persistence)
 * - Whether the payment form is processing
 *
 * Note: provider tokens (Stripe pm_/SetupIntent, Square nonce) are NOT held
 * here — both providers hand their token/id straight to the caller via the
 * add-card form's `onSuccess(token)` callback, so the context has no token
 * fields to read back.
 *
 * @module services/internalApi/payment/context
 */

import React, { createContext, useCallback, useMemo, useState } from 'react';

/**
 * Payment state interface
 */
export interface PaymentState {
  /** User's email for payment */
  email: string;
  /** Whether payment form is currently processing */
  isProcessing: boolean;
}

/**
 * Payment context value interface
 */
export interface PaymentContextValue {
  /** Current payment state */
  state: PaymentState;
  /** Set user email */
  setEmail: (email: string) => void;
  /** Set processing state */
  setIsProcessing: (processing: boolean) => void;
}

const PaymentContext = createContext<PaymentContextValue | null>(null);

/**
 * Session storage keys
 */
const STORAGE_KEYS = {
  EMAIL: 'lgc_booking_payment_email',
} as const;

/**
 * Initial payment state
 */
const INITIAL_STATE: PaymentState = {
  email: '',
  isProcessing: false,
};

/**
 * Load email from session storage
 */
function loadEmailFromStorage(): string {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.EMAIL) || '';
  } catch (error) {
    console.warn('Failed to load email from session storage:', error);
    return '';
  }
}

/**
 * Save email to session storage
 */
function saveEmailToStorage(email: string): void {
  try {
    if (email) {
      sessionStorage.setItem(STORAGE_KEYS.EMAIL, email);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.EMAIL);
    }
  } catch (error) {
    console.warn('Failed to save email to session storage:', error);
  }
}

/**
 * Payment provider component
 */
export function PaymentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PaymentState>(() => ({
    ...INITIAL_STATE,
    email: loadEmailFromStorage(),
  }));

  const setEmail = useCallback((email: string) => {
    setState(prev => ({ ...prev, email }));
    saveEmailToStorage(email);
  }, []);

  const setIsProcessing = useCallback((processing: boolean) => {
    setState(prev => ({ ...prev, isProcessing: processing }));
  }, []);

  const value = useMemo<PaymentContextValue>(
    () => ({
      state,
      setEmail,
      setIsProcessing,
    }),
    [state, setEmail, setIsProcessing]
  );

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
}

export { PaymentContext };
